// How-it-works page copy — en / fr / es. Every engagement starts with the free
// Process Mapping Analysis Audit (up to 2 meetings), then the five calm steps.

export const TRIAL_COPY = {
  en: {
    docTitle: "How It Works — From Free Audit to Measured Capacity | Ocean Data",
    eyebrow: "How it works · the audit comes first",
    h1a: "We learn. We simplify.",
    h1b: "You see value.",
    heroSub:
      "Five calm steps, starting with a free Process Mapping Analysis Audit — up to two meetings where we map one critical process on your real numbers. We make the systems work so your people can do what they do best — and the first live answer usually arrives inside ten minutes.",
    stepLabel: (i) => `Step ${i + 1}`,
    openLabel: "Open",
    tapLabel: "Tap",
    youSide: "From your side",
    ourSide: "From our side",
    stages: [
      {
        id: "stage-show",
        when: "Meeting 1 · one 45-minute conversation",
        title: "Show us how work flows today",
        ask: "Tell us one story: a recent job, from first call to final invoice.",
        youDo: "You talk. We listen and map how a lead becomes a sale, becomes a project, becomes an invoice — as it really happens, not as the process chart says.",
        weDo: "We sketch your flow live on the call and confirm it with you before we touch anything.",
        sticky: "One story about one job. That's all we need to begin.",
      },
      {
        id: "stage-find",
        when: "Between meetings · we work, you don't",
        title: "We find the repetitive work",
        ask: "Nothing to prepare. We work inside your Zoho CRM, Projects, and Books.",
        youDo: "Carry on with your week. We may ask one or two short questions by message.",
        weDo: "We trace every place information gets chased, copied, or re-entered, and hand you a one-page map of where your team's hours are going. That map is the audit deliverable — yours to keep.",
        sticky: "You'll recognise your whole week on a single page.",
      },
      {
        id: "stage-activate",
        when: "The engagement begins · quiet setup",
        title: "We connect one critical process",
        ask: "Approve one thing: the process worth fixing first.",
        youDo: "A five-minute yes. Usually it's the sales-to-project handoff, or the books.",
        weDo: "We make your existing tools share one record around that process — starting in a safe practice mode, so your team can try it before anything real changes.",
        sticky: "One process starts flowing. Nothing else changes yet.",
      },
      {
        id: "stage-see",
        when: "First week · the moment it clicks",
        title: "You see the first live answer",
        ask: "Ask the question you ask every week — out loud, on your own numbers.",
        youDo: "Watch your question answered from your live record, usually inside the first ten minutes of the activation call.",
        weDo: "We stay on the line while you try to stump it. Most owners can't.",
        sticky: "The question you ask every week, answered while the coffee's hot.",
        peak: true,
      },
      {
        id: "stage-measure",
        when: "Every week · proof, then a decision",
        title: "We measure capacity, then expand",
        ask: "Read one short report each week: hours returned, per seat.",
        youDo: "Feel the week get lighter. Hold each measured report and decide what happens next.",
        weDo: "We report the hours returned every week and suggest the next process only when the numbers justify it. The audit map is yours to keep, whatever you decide.",
        sticky: "The map is yours before you pay anything. Always.",
      },
    ],
    noteTitle: "And then it starts helping before you ask.",
    noteBody:
      "From the first week, the system quietly surfaces the next useful thing — a customer going quiet, an invoice ready to send, a date about to slip — so the value compounds without you learning anything new.",
    noteLink: "Feel it in the live demos →",
    closeEyebrow: "First live answer in under 10 minutes",
    closeTitle: "A free audit first, calmly mapped. Nothing to lose but the chasing.",
    closeBody: "One conversation starts it. The process map from your audit is yours to keep either way.",
    closePricingLink: "See the simple pricing →",
    closePricingTo: "/pricing",
    closeCta: "Book my free audit",
    schemaName: "How an Ocean Data engagement works",
    schemaDesc:
      "A calm five-step engagement starting with a free Process Mapping Analysis Audit: we learn your flow, find the repetitive work, connect one critical process, and measure the capacity returned. First live answer typically inside ten minutes.",
    schemaLang: "en-CA",
    hreflangs: { "en-CA": "/how-it-works", "fr-CA": "/fr/comment-ca-marche", es: "/es/como-funciona", "x-default": "/how-it-works" },
  },
  fr: {
    docTitle: "Comment ça marche — de l'audit gratuit à la capacité mesurée | Ocean Data",
    eyebrow: "Comment ça marche · l'audit d'abord",
    h1a: "On apprend. On simplifie.",
    h1b: "Vous voyez la valeur.",
    heroSub:
      "Cinq étapes calmes, à commencer par un audit d'analyse de processus gratuit — jusqu'à deux rencontres où l'on cartographie un processus critique sur vos vrais chiffres. On fait travailler les systèmes pour que vos gens fassent ce qu'ils font de mieux — et la première réponse en direct arrive généralement en moins de dix minutes.",
    stepLabel: (i) => `Étape ${i + 1}`,
    openLabel: "Ouvert",
    tapLabel: "Voir",
    youSide: "De votre côté",
    ourSide: "De notre côté",
    stages: [
      {
        id: "stage-show",
        when: "Rencontre 1 · une conversation de 45 minutes",
        title: "Montrez-nous comment le travail circule aujourd'hui",
        ask: "Racontez-nous une histoire : un contrat récent, du premier appel à la dernière facture.",
        youDo: "Vous parlez. On écoute et on cartographie comment un prospect devient une vente, un projet, une facture — tel que ça se passe vraiment, pas tel que l'organigramme le dit.",
        weDo: "On dessine votre flux en direct pendant l'appel et on le confirme avec vous avant de toucher quoi que ce soit.",
        sticky: "Une histoire sur un contrat. C'est tout ce qu'il nous faut pour commencer.",
      },
      {
        id: "stage-find",
        when: "Entre les rencontres · on travaille, pas vous",
        title: "On trouve le travail répétitif",
        ask: "Rien à préparer. On travaille dans vos outils existants.",
        youDo: "Continuez votre semaine. On posera peut-être une ou deux courtes questions par message.",
        weDo: "On trace chaque endroit où l'information est poursuivie, copiée ou ressaisie, et on vous remet une carte d'une page de là où vont les heures de votre équipe. Cette carte est le livrable de l'audit — elle reste à vous.",
        sticky: "Vous reconnaîtrez toute votre semaine sur une seule page.",
      },
      {
        id: "stage-activate",
        when: "Début du mandat · installation discrète",
        title: "On connecte un processus critique",
        ask: "Approuvez une chose : le processus à réparer d'abord.",
        youDo: "Un oui de cinq minutes. C'est généralement le passage vente→projet, ou la comptabilité.",
        weDo: "On fait partager un même dossier à vos outils existants autour de ce processus — en mode pratique sécuritaire d'abord, pour que l'équipe l'essaie avant que quoi que ce soit change pour de vrai.",
        sticky: "Un processus se met à couler. Rien d'autre ne change encore.",
      },
      {
        id: "stage-see",
        when: "Première semaine · le déclic",
        title: "Vous voyez la première réponse en direct",
        ask: "Posez la question que vous posez chaque semaine — à voix haute, sur vos propres chiffres.",
        youDo: "Regardez votre question trouver sa réponse dans votre dossier en direct, généralement dans les dix premières minutes de l'appel.",
        weDo: "On reste en ligne pendant que vous essayez de la piéger. La plupart des dirigeants n'y arrivent pas.",
        sticky: "La question de chaque semaine, répondue pendant que le café est chaud.",
        peak: true,
      },
      {
        id: "stage-measure",
        when: "Chaque semaine · la preuve, puis la décision",
        title: "On mesure la capacité, puis on élargit",
        ask: "Lisez un court rapport chaque semaine : les heures rendues, par personne.",
        youDo: "Sentez la semaine s'alléger. Tenez chaque rapport mesuré et décidez de la suite.",
        weDo: "On rapporte les heures rendues chaque semaine et on ne suggère le processus suivant que quand les chiffres le justifient. La carte de l'audit reste à vous, quoi que vous décidiez.",
        sticky: "La carte est à vous avant de payer quoi que ce soit. Toujours.",
      },
    ],
    noteTitle: "Et ensuite, le système aide avant même qu'on demande.",
    noteBody:
      "Dès la première semaine, le système fait tranquillement remonter la prochaine chose utile — un client qui se tait, une facture prête à partir, une date qui glisse — pour que la valeur s'accumule sans que vous appreniez quoi que ce soit de nouveau.",
    noteLink: "Sentez-le dans les démos en direct →",
    closeEyebrow: "Première réponse en direct en moins de 10 minutes",
    closeTitle: "Un audit gratuit d'abord, calmement cartographié. Rien à perdre sauf la poursuite.",
    closeBody: "Une conversation suffit pour commencer. La carte de processus de votre audit reste à vous dans tous les cas.",
    closePricingLink: "Voir les tarifs simples →",
    closePricingTo: "/fr/tarifs",
    closeCta: "Réserver mon audit gratuit",
    schemaName: "Comment se déroule un mandat Ocean Data",
    schemaDesc:
      "Un mandat calme en cinq étapes commençant par un audit d'analyse de processus gratuit : on apprend votre flux, on trouve le travail répétitif, on connecte un processus critique et on mesure la capacité rendue. Première réponse en direct généralement en moins de dix minutes.",
    schemaLang: "fr-CA",
    hreflangs: { "en-CA": "/how-it-works", "fr-CA": "/fr/comment-ca-marche", es: "/es/como-funciona", "x-default": "/how-it-works" },
  },
  es: {
    docTitle: "Cómo funciona — de la auditoría gratuita a la capacidad medida | Ocean Data",
    eyebrow: "Cómo funciona · la auditoría primero",
    h1a: "Aprendemos. Simplificamos.",
    h1b: "Usted ve el valor.",
    heroSub:
      "Cinco pasos tranquilos, empezando por una auditoría gratuita de análisis y mapeo de procesos — hasta dos reuniones donde mapeamos un proceso crítico sobre sus cifras reales. Hacemos que los sistemas trabajen para que su gente haga lo que mejor sabe hacer — y la primera respuesta en vivo suele llegar en menos de diez minutos.",
    stepLabel: (i) => `Paso ${i + 1}`,
    openLabel: "Abierto",
    tapLabel: "Ver",
    youSide: "De su lado",
    ourSide: "De nuestro lado",
    stages: [
      {
        id: "stage-show",
        when: "Reunión 1 · una conversación de 45 minutos",
        title: "Muéstrenos cómo fluye el trabajo hoy",
        ask: "Cuéntenos una historia: un trabajo reciente, de la primera llamada a la última factura.",
        youDo: "Usted habla. Nosotros escuchamos y mapeamos cómo un prospecto se vuelve venta, proyecto, factura — como realmente pasa, no como dice el diagrama.",
        weDo: "Dibujamos su flujo en vivo durante la llamada y lo confirmamos con usted antes de tocar nada.",
        sticky: "Una historia sobre un trabajo. Es todo lo que necesitamos para empezar.",
      },
      {
        id: "stage-find",
        when: "Entre reuniones · trabajamos nosotros, no usted",
        title: "Encontramos el trabajo repetitivo",
        ask: "Nada que preparar. Trabajamos dentro de sus herramientas existentes.",
        youDo: "Siga con su semana. Quizá hagamos una o dos preguntas cortas por mensaje.",
        weDo: "Rastreamos cada lugar donde la información se persigue, se copia o se reingresa, y le entregamos un mapa de una página de adónde van las horas de su equipo. Ese mapa es el entregable de la auditoría — es suyo.",
        sticky: "Reconocerá toda su semana en una sola página.",
      },
      {
        id: "stage-activate",
        when: "Comienzo del proyecto · instalación silenciosa",
        title: "Conectamos un proceso crítico",
        ask: "Apruebe una cosa: el proceso que vale la pena arreglar primero.",
        youDo: "Un sí de cinco minutos. Normalmente es el traspaso venta→proyecto, o la contabilidad.",
        weDo: "Hacemos que sus herramientas existentes compartan un mismo expediente alrededor de ese proceso — empezando en un modo de práctica seguro, para que el equipo lo pruebe antes de que algo real cambie.",
        sticky: "Un proceso empieza a fluir. Nada más cambia todavía.",
      },
      {
        id: "stage-see",
        when: "Primera semana · el momento en que hace clic",
        title: "Usted ve la primera respuesta en vivo",
        ask: "Haga la pregunta que hace cada semana — en voz alta, sobre sus propias cifras.",
        youDo: "Vea su pregunta respondida desde su expediente en vivo, normalmente dentro de los primeros diez minutos de la llamada de activación.",
        weDo: "Nos quedamos en la línea mientras intenta tomarlo por sorpresa. La mayoría de los dueños no lo logra.",
        sticky: "La pregunta de cada semana, respondida mientras el café está caliente.",
        peak: true,
      },
      {
        id: "stage-measure",
        when: "Cada semana · evidencia, luego una decisión",
        title: "Medimos la capacidad, luego ampliamos",
        ask: "Lea un informe corto cada semana: horas devueltas, por persona.",
        youDo: "Sienta la semana hacerse más ligera. Tenga cada informe medido en la mano y decida qué sigue.",
        weDo: "Reportamos las horas devueltas cada semana y sugerimos el siguiente proceso solo cuando los números lo justifican. El mapa de la auditoría es suyo, decida lo que decida.",
        sticky: "El mapa es suyo antes de pagar nada. Siempre.",
      },
    ],
    noteTitle: "Y luego empieza a ayudar antes de que usted pida.",
    noteBody:
      "Desde la primera semana, el sistema saca a la superficie la siguiente cosa útil — un cliente que se queda callado, una factura lista para enviar, una fecha a punto de escaparse — para que el valor se acumule sin que usted aprenda nada nuevo.",
    noteLink: "Siéntalo en las demos en vivo →",
    closeEyebrow: "Primera respuesta en vivo en menos de 10 minutos",
    closeTitle: "Una auditoría gratuita primero, mapeada con calma. Nada que perder excepto la persecución.",
    closeBody: "Una conversación lo inicia. El mapa de procesos de su auditoría es suyo en cualquier caso.",
    closePricingLink: "Ver los precios simples →",
    closePricingTo: "/es/precios",
    closeCta: "Reservar mi auditoría gratuita",
    schemaName: "Cómo funciona un proyecto con Ocean Data",
    schemaDesc:
      "Un proyecto tranquilo de cinco pasos que empieza con una auditoría gratuita de análisis y mapeo de procesos: aprendemos su flujo, encontramos el trabajo repetitivo, conectamos un proceso crítico y medimos la capacidad devuelta. Primera respuesta en vivo normalmente en menos de diez minutos.",
    schemaLang: "es",
    hreflangs: { "en-CA": "/how-it-works", "fr-CA": "/fr/comment-ca-marche", es: "/es/como-funciona", "x-default": "/how-it-works" },
  },
};
